'use client';
import { useState, useEffect, useCallback, useRef, RefObject } from 'react';

interface LiteSliderOptions {
  totalSlides: number;
  autoplay?: boolean;
  delay?: number;
  duration?: number;
  type?: 'slide' | 'fade';
  isOverlap?: boolean;
}

const DEFAULT_TIMES = {
  duration: 300 / 1000,
  delay: 3000,
};

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

const useLiteSlider = ({
  type,
  totalSlides,
  autoplay = false,
  delay = DEFAULT_TIMES.delay,
  duration = DEFAULT_TIMES.duration,
  isOverlap,
}: LiteSliderOptions) => {
  const [windowWidth, setWindowWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isForward, setIsForward] = useState(true); // 슬라이드 방향
  const [slideWidth, setSlideWidth] = useState(0); // 슬라이드 폭
  const [slideDuration, setSlideDuration] = useState(duration); // 슬라이드 트랜지션 시간
  const [slideTransitionTimeFunc, setSlideTransitionTimeFunc] = useState(''); // 슬라이드 트랜지션 타임펑션
  const sliderRef = useRef<HTMLDivElement>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialized = useRef(false);

  // 슬라이더 폭 계산
  const calculateSlideWidthHeight = useCallback(() => {
    const slideContainer = sliderRef.current as HTMLElement;
    const sliderElement = slideContainer.children[0] as HTMLElement;
    const computedStyle = window.getComputedStyle(sliderElement);
    if (type === 'fade') {
      // 패널들을 배열로 변환 (패널들이 HTMLElement로 처리되도록 타입 캐스팅)
      const panels = Array.from(sliderElement.children) as HTMLElement[];
      // 모든 패널 중 가장 높은 높이를 계산
      const maxHeight = Math.max(...panels.map((panel) => panel.getBoundingClientRect().height));
      // 패널랩퍼의 높이를 가장 높은 패널의 높이로 설정
      // sliderElement.style.height = `${maxHeight}px`;
      slideContainer.style.setProperty('--panel-outer-height', `${maxHeight.toFixed(2)}px`);
    } else if (type === 'slide') {
      // 초기 상태 설정 (복제 후 슬라이더 패널 위치 설정)
      // const computedStyle = window.getComputedStyle(sliderElement);
      const gap = parseInt(computedStyle.gap) || 0;
      const panelWidth = sliderElement.offsetWidth + gap;
      sliderElement.style.transform = `translateX(-${panelWidth}px)`;
      setSlideWidth(panelWidth);

      setTimeout(() => {
        const durationTime = computedStyle.getPropertyValue('--panel-duration-time');
        const durationTimeFunc = computedStyle.getPropertyValue(
          '--panel-transition-timing-function',
        );
        setSlideDuration(parseFloat(durationTime));
        setSlideTransitionTimeFunc(durationTimeFunc);
      }, 0);
    }
  }, []);

  // 타입이 슬라이드 경우 무한 Carousel 효과를 위해 순수 DOM 요소 복제
  useEffect(() => {
    if (type === 'slide' && sliderRef.current && !isInitialized.current) {
      const slideContainer = sliderRef.current as HTMLElement;
      const sliderElement = slideContainer.children[0] as HTMLElement;
      const panels = Array.from(sliderElement.children);

      if (panels.length > 0) {
        // 복제 로직: 슬라이더의 처음과 끝 패널 복제
        const first = panels[0].cloneNode(true) as HTMLElement;
        const last = panels[panels.length - 1].cloneNode(true) as HTMLElement;
        first.classList.remove('active');

        // 복제된 패널 추가
        sliderElement.insertBefore(last, panels[0]);
        sliderElement.append(first);
        calculateSlideWidthHeight();
        isInitialized.current = true; // 초기화 완료 표시
      }
    }
  }, []);

  // 리사이즈 이벤트
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // 창 크기 변화 시 다시 패널의 높이 또는 폭을  계산
      if (sliderRef.current && sliderRef.current.children) {
        calculateSlideWidthHeight();
      }
    };

    // 디바운스 적용 (300ms 딜레이)
    const debouncedHandleResize = debounce(handleResize, 300);
    window.addEventListener('resize', debouncedHandleResize);
    handleResize();
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, []);

  // 슬라이드 전환 애니메이션
  const transitionSlide = useCallback(() => {
    const slideContainer = sliderRef.current as HTMLElement;
    const sliderElement = slideContainer.children[0] as HTMLElement;
    if (!sliderElement || slideWidth === 0) return;

    const totalPanelIndex = totalSlides;
    let moveValue;
    if (type === 'slide') {
      if (currentIndex === 0 && isForward) {
        // console.log('마지막 슬라이드에서 첫 번째 슬라이드로 이동');
        sliderElement.style.transition = 'none';
        sliderElement.style.transform = `translateX(0)`;

        setTimeout(() => {
          sliderElement.style.transition = `transform ${slideDuration}s ${slideTransitionTimeFunc}`;
          sliderElement.style.transform = `translateX(-${slideWidth}px)`;
        }, 0);
      } else if (currentIndex === totalPanelIndex - 1 && !isForward) {
        // console.log('첫 번째 슬라이드에서 마지막 슬라이드로 이동');
        sliderElement.style.transition = 'none';
        sliderElement.style.transform = `translateX(-${slideWidth * (totalPanelIndex + 1)}px)`;

        // 트랜지션 후 마지막 슬라이드로 자연스럽게 이동
        setTimeout(() => {
          sliderElement.style.transition = `transform ${slideDuration}s ${slideTransitionTimeFunc}`;
          sliderElement.style.transform = `translateX(-${slideWidth * totalPanelIndex}px)`;
        }, 0);
      } else {
        // 기본 슬라이드 이동
        moveValue = slideWidth * (currentIndex + 1);
        sliderElement.style.transform = `translateX(-${moveValue}px)`;
      }
    } else if (type === 'fade') {
      // 페이드 전환
      Array.from(sliderElement.children).forEach((child, index) => {
        child.classList.toggle('active', index === currentIndex);
      });
    }
  }, [currentIndex, isForward]);

  useEffect(() => {
    transitionSlide();
  }, [currentIndex]);

  const slideTo = useCallback(
    (index: number) => {
      setIsForward(index > currentIndex);
      setCurrentIndex(index);
      transitionSlide();
    },
    [totalSlides],
  );

  const nextSlide = useCallback(() => {
    setIsForward(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    transitionSlide();
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setIsForward(false);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
    transitionSlide();
  }, [totalSlides]);

  const startAutoplay = useCallback(() => {
    if (autoplay && !intervalIdRef.current && delay >= DEFAULT_TIMES.delay) {
      const id = setInterval(nextSlide, delay);
      intervalIdRef.current = id;
    }
  }, [autoplay, delay, nextSlide]);

  const stopAutoplay = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  return {
    currentIndex,
    isForward,
    slideTo,
    nextSlide,
    prevSlide,
    startAutoplay,
    stopAutoplay,
    sliderRef,
  };
};

export default useLiteSlider;
